// SyncQueue Repository for Dexie Operations
// Handles sync queue data persistence in IndexedDB for offline-first functionality

import type { SyncJob } from '@/services/sync/syncService';
import { db } from '@/db/dexieClient';
import { uow } from '@/db/unitOfWork';

// --- Public API: Get Operations ---
export const getPendingAndFailedJobs = async (deadLetterThreshold: number): Promise<SyncJob[]> => {
  try {
    return await db.syncQueue
      .where('status')
      .equals('pending')
      .or('status')
      .equals('failed')
      .filter((job) => job.retryCount < deadLetterThreshold)
      .toArray();
  } catch (error) {
    console.error('Failed to get pending and failed jobs:', error);
    console.warn('[syncQueueRepository] Failed to retrieve pending and failed jobs:', {
      error: error instanceof Error ? error.message : String(error),
      deadLetterThreshold,
    });
    return [];
  }
};

export const getFailedJobsExceedingThreshold = async (
  deadLetterThreshold: number
): Promise<SyncJob[]> => {
  try {
    return await db.syncQueue
      .where('status')
      .equals('failed')
      .filter((job) => job.retryCount >= deadLetterThreshold)
      .toArray();
  } catch (error) {
    console.error('Failed to get failed jobs exceeding threshold:', error);
    console.warn('[syncQueueRepository] Failed to retrieve jobs exceeding threshold:', {
      error: error instanceof Error ? error.message : String(error),
      deadLetterThreshold,
    });
    return [];
  }
};

export const getSyncStatusCounts = async (): Promise<{
  pendingCount: number;
  failedCount: number;
  deadLetterCount: number;
}> => {
  try {
    const pendingCount = await db.syncQueue.where('status').equals('pending').count();
    const failedCount = await db.syncQueue.where('status').equals('failed').count();
    const deadLetterCount = await db.syncQueue.where('status').equals('dead-letter').count();

    return {
      pendingCount,
      failedCount,
      deadLetterCount,
    };
  } catch (error) {
    console.error('Failed to get sync status counts:', error);
    console.warn('[syncQueueRepository] Failed to get sync status counts:', {
      error: error instanceof Error ? error.message : String(error),
    });
    return {
      pendingCount: 0,
      failedCount: 0,
      deadLetterCount: 0,
    };
  }
};

// --- Public API: Update Operations ---
export const markAsSyncing = async (id: string): Promise<void> => {
  try {
    await uow.runRW([db.syncQueue], async () => {
      await db.syncQueue.update(id, { status: 'syncing' });
    });
  } catch (error) {
    console.error(`Failed to mark job as syncing (${id}):`, error);
    console.warn('[syncQueueRepository] Failed to mark job as syncing:', {
      jobId: id,
      error: error instanceof Error ? error.message : String(error),
    });
    throw error;
  }
};

export const markAsSynced = async (id: string, syncedAt: Date): Promise<void> => {
  try {
    await uow.runRW([db.syncQueue], async () => {
      await db.syncQueue.update(id, {
        status: 'synced',
        syncedAt,
      });
    });
  } catch (error) {
    console.error(`Failed to mark job as synced (${id}):`, error);
    console.warn('[syncQueueRepository] Failed to mark job as synced:', {
      jobId: id,
      syncedAt,
      error: error instanceof Error ? error.message : String(error),
    });
    throw error;
  }
};

export const markAsFailed = async (
  id: string,
  retryCount: number,
  error: string
): Promise<void> => {
  try {
    await uow.runRW([db.syncQueue], async () => {
      await db.syncQueue.update(id, {
        status: 'failed',
        retryCount,
        error,
      });
    });
  } catch (error) {
    console.error(`Failed to mark job as failed (${id}):`, error);
    console.warn('[syncQueueRepository] Failed to mark job as failed:', {
      jobId: id,
      retryCount,
      errorMessage: error,
      dbError: error instanceof Error ? error.message : String(error),
    });
    throw error;
  }
};

export const moveToDeadLetter = async (id: string, retryCount: number): Promise<void> => {
  try {
    await uow.runRW([db.syncQueue], async () => {
      await db.syncQueue.update(id, {
        status: 'dead-letter',
        error: `Job moved to dead letter queue after ${retryCount} failed attempts`,
      });
    });
  } catch (error) {
    console.error(`Failed to move job to dead letter (${id}):`, error);
    console.warn('[syncQueueRepository] Failed to move job to dead letter:', {
      jobId: id,
      retryCount,
      error: error instanceof Error ? error.message : String(error),
    });
    throw error;
  }
};

// --- Public API: Add Operations ---
export const addSyncJob = async (job: SyncJob): Promise<void> => {
  try {
    await uow.runRW([db.syncQueue], async () => {
      await db.syncQueue.add(job);
    });
  } catch (error) {
    console.error('Failed to add sync job:', error);
    console.warn('[syncQueueRepository] Failed to add sync job to queue:', {
      jobId: job.id,
      jobType: job.type,
      jobStatus: job.status,
      error: error instanceof Error ? error.message : String(error),
    });
    throw error;
  }
};
